import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import { useSDAInvite } from "./SDAContext";
import { QuoteAnswers } from "../../api/services";

const US_DESCRIPTIONS = [
  { value: "self-employed", labelKey: "sda.underwriting.options.us.selfEmployed" },
  { value: "student-retired", labelKey: "sda.underwriting.options.us.studentRetired" },
  { value: "relocating", labelKey: "sda.underwriting.options.us.relocating" },
  { value: "other", labelKey: "sda.underwriting.options.us.other" },
] as const;

const NON_US_DESCRIPTIONS = [
  { value: "currently-employed", labelKey: "sda.underwriting.options.nonUs.currentlyEmployed" },
  { value: "student", labelKey: "sda.underwriting.options.nonUs.student" },
  { value: "other", labelKey: "sda.underwriting.options.nonUs.other" },
] as const;

const parseCurrency = (s: string): number | undefined => {
  const cleaned = s.replace(/[^\d]/g, "");
  return cleaned ? parseInt(cleaned, 10) : undefined;
};

const SDAUnderwriting: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { invite, setAnswers } = useSDAInvite();
  const [isUSResident, setIsUSResident] = useState<boolean | null>(null);
  const [userType, setUserType] = useState<string>("");
  const [ssn, setSsn] = useState<string>("");
  const [annualIncome, setAnnualIncome] = useState<string>("");
  const [monthlyRent, setMonthlyRent] = useState<string>("");

  // If the invite already has a quote, the underwriting questions are
  // locked. Bounce the user forward to the quote screen.
  useEffect(() => {
    if (invite?.quoteId) {
      navigate(`/sda/${uuid}/quote`, { replace: true });
    }
  }, [invite?.quoteId, navigate, uuid]);

  const canContinue = (() => {
    if (isUSResident === null) return false;
    if (!userType) return false;
    if (!isUSResident && userType === "currently-employed") {
      return !!parseCurrency(annualIncome) && !!parseCurrency(monthlyRent);
    }
    return true;
  })();

  const handleNext = () => {
    if (isUSResident === null || !userType) return;

    const answers: QuoteAnswers = {
      usResident: isUSResident,
    };

    if (isUSResident) {
      answers.bestDescription = userType as QuoteAnswers["bestDescription"];
      answers.ssn = ssn || undefined;
    } else {
      answers.nonUsBestDescription =
        userType as QuoteAnswers["nonUsBestDescription"];
      if (userType === "currently-employed") {
        answers.annualIncome = parseCurrency(annualIncome);
        answers.monthlyRent = parseCurrency(monthlyRent);
      }
    }

    setAnswers(answers);
    navigate(`/sda/${uuid}/quote`);
  };

  const formatSSN = (value: string) => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue.length <= 3) return cleanValue;
    if (cleanValue.length <= 5)
      return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3)}`;
    return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 5)}-${cleanValue.slice(5, 9)}`;
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSsn(formatSSN(e.target.value));
  };

  const formatCurrency = (value: string) => {
    const cleanValue = value.replace(/[^\d]/g, "");
    return cleanValue
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(parseInt(cleanValue))
      : "";
  };

  const renderRadioCard = (
    label: string,
    checked: boolean,
    onChange: () => void,
    name: string,
  ) => {
    const id = `${name}-${label}`.replace(/[^a-zA-Z]/g, "");
    return (
      <label
        key={id}
        htmlFor={id}
        className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 text-sm transition-all ${
          checked
            ? "border-primary bg-primary/5"
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <input
          id={id}
          type="radio"
          name={name}
          checked={checked}
          onChange={onChange}
          className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
        />
        <span
          className={checked ? "font-medium text-primary" : "text-gray-700"}
        >
          {label}
        </span>
      </label>
    );
  };

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={4} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {t("sda.underwriting.title")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {t("sda.underwriting.subtitle")}
          </p>
        </div>

        <div className="space-y-6 p-5">
          {/* US Resident */}
          <div>
            <p className="mb-3 text-sm font-semibold text-primary">
              {t("sda.underwriting.usResidentQuestion")}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              {renderRadioCard(
                t("sda.underwriting.yes"),
                isUSResident === true,
                () => {
                  setIsUSResident(true);
                  setUserType("");
                  setAnnualIncome("");
                  setMonthlyRent("");
                },
                "usResident",
              )}
              {renderRadioCard(
                t("sda.underwriting.no"),
                isUSResident === false,
                () => {
                  setIsUSResident(false);
                  setUserType("");
                  setSsn("");
                  setAnnualIncome("");
                  setMonthlyRent("");
                },
                "usResident",
              )}
            </div>
          </div>

          {/* What best describes you */}
          {isUSResident !== null && (
            <div>
              <p className="mb-3 text-sm font-semibold text-primary">
                {t("sda.underwriting.bestDescription")}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(isUSResident ? US_DESCRIPTIONS : NON_US_DESCRIPTIONS).map(
                  (option) =>
                    renderRadioCard(
                      t(option.labelKey),
                      userType === option.value,
                      () => {
                        setUserType(option.value);
                        if (!isUSResident && option.value !== "currently-employed") {
                          setAnnualIncome("");
                          setMonthlyRent("");
                        }
                      },
                      "userType",
                    ),
                )}
              </div>
            </div>
          )}

          {/* SSN */}
          {isUSResident && userType && (
            <div>
              <label
                htmlFor="ssn"
                className="mb-1.5 block text-sm font-semibold text-primary"
              >
                {t("sda.underwriting.ssn")}
              </label>
              <input
                type="text"
                id="ssn"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none sm:max-w-xs"
                placeholder={t("sda.underwriting.ssnPlaceholder")}
                value={ssn}
                onChange={handleSSNChange}
                maxLength={11}
              />
            </div>
          )}

          {/* Financial info for non-US employed */}
          {!isUSResident && userType === "currently-employed" && (
            <div>
              <p className="mb-3 text-sm font-semibold text-primary">
                {t("sda.underwriting.financialInfo")}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="annualIncome"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    {t("sda.underwriting.annualIncome")}
                  </label>
                  <input
                    type="text"
                    id="annualIncome"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    placeholder="$0"
                    value={annualIncome}
                    onChange={(e) =>
                      setAnnualIncome(formatCurrency(e.target.value))
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor="monthlyRent"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    {t("sda.underwriting.monthlyRent")}
                  </label>
                  <input
                    type="text"
                    id="monthlyRent"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    placeholder="$0"
                    value={monthlyRent}
                    onChange={(e) =>
                      setMonthlyRent(formatCurrency(e.target.value))
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <SDATestSkipButton
            onSkip={() => navigate(`/sda/${uuid}/quote`)}
          />
          <button
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canContinue}
            onClick={handleNext}
          >
            {t("sda.underwriting.next")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SDAUnderwriting;
